// Public plan book. Homepage cards, /pricing, and checkout intents read
// this file. List prices live here only — do not retype them in pages.

export const PILOT_MAILTO = 'mailto:pilots@nventr.io?subject=agentOS%20pilot';
export const SALES_MAILTO = 'mailto:hello@nventr.io?subject=Book%20a%20demo';

export const PILOT = {
  label: 'First pipeline with us',
  helper: 'We stand up the first pipeline with you — $9k–$24k. Not a plan.',
  href: PILOT_MAILTO,
};

export const PLATFORM_TIERS = [
  {
    id: 'free',
    name: 'Free',
    product: 'Platform Free',
    price: '$0',
    href: '/start?plan=free',
    cta: 'Try the canvas free',
    helper: '$0 · canvas only · no production runs · no card',
    bundle: [
      'Shared nQube canvas',
      '1 builder · viewers free',
      'No production runs · no production connectors',
    ],
    who: 'Try the design surface.',
  },
  {
    id: 'start',
    name: 'Start',
    product: 'Platform Start',
    price: '$15 / mo',
    featured: true,
    href: '/start?plan=start',
    cta: 'Start Platform · $15',
    helper: '$15/mo · 1 builder · 3,000 workflow runs',
    bundle: [
      '1 builder · viewers free',
      '3,000 workflow runs/mo',
      'Shared nQube + production connectors · bring your own model',
      'No dedicated URL, database, or repo',
    ],
    who: 'Platform entry — hosted workflow runs. Not an instance.',
  },
  {
    id: 'team',
    name: 'Team',
    product: 'Platform Team',
    price: '$39 / mo',
    href: '/orgs?plan=team',
    cta: 'Start Platform Team',
    helper: '$39/mo · 3 builders · 12,000 workflow runs',
    bundle: [
      '3 builders + 10 operators · viewers free',
      '12,000 workflow runs/mo',
      'Shared nQube + production connectors',
      'No dedicated URL / database / layer repo',
    ],
    who: 'Platform — gated writes into systems you already use.',
  },
  {
    id: 'business',
    name: 'Business',
    price: '$599 / mo',
    bundle: [
      'Multi-workflow · SAML',
      '10 builders + 25 operators · viewers free',
      '40,000 workflow runs/mo',
      'Still no instance — that is Kit',
    ],
    who: 'Department ops / finance. SAML lives here, not on Team.',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Typical year-1 from $8,000/mo',
    bundle: [
      '25 builders + 100 operators · viewers free',
      '400,000 Runtime Credits / mo ≈ 80,000 invoice-style jobs or ≈ 40,000 ticket conversations',
      'SCIM · named support · optional VPC nodes',
      'Instance rent is a Kit line',
    ],
    who: 'Sales floor on Platform.',
  },
];

export const KIT_TIERS = [
  {
    id: 'build',
    name: 'Kit Site',
    product: 'Kit Site',
    price: '$15 / mo',
    href: '/orgs?plan=build',
    cta: 'Start Kit Site',
    helper: '$15/mo · shared host · 120 build credits',
    bundle: [
      'Public site on a shared host',
      '120 build credits',
      'Export the layer you generate',
      'No dedicated database, workers, or Dev Console',
    ],
    who: 'Public site on a shared host. The dedicated instance is Kit, the next card.',
  },
  {
    id: 'kit',
    name: 'Kit',
    product: 'App Kit',
    price: '$149 / mo',
    featured: true,
    href: '/start?plan=kit',
    cta: 'Launch a Kit instance',
    helper: '$149/mo license · your URL, database, and repo · viewers free',
    included: { devRuns: 8, computeHours: 200 },
    bundle: [
      'License for the foundation — URL, database, layer repo',
      'Public site is the front door. Sign-in and books stay on this instance',
      'Dev Console included — 8 runs a month, then metered. Ask for a change, review the diff, deploy',
      'Large files and heavy jobs, health-gated deploys, foundation updates',
      'Managed host included. Your cloud or on-prem keeps the license and drops host meters',
    ],
    who: 'App Kit — an app people log into.',
  },
  {
    id: 'kit-business',
    name: 'Kit Business',
    price: '$349 / mo',
    included: { devRuns: 40, computeHours: 800 },
    bundle: [
      'Production instance · unlimited viewers on this instance',
      'Same license. Your cloud or on-prem drops host meters',
      '40 Dev Console runs · 800 compute hours on a managed host',
      'SAML on the instance',
      'Still not org-wide Platform pipelines',
    ],
    who: 'Org-wide nQube is a Platform line.',
  },
  {
    id: 'kit-enterprise',
    name: 'Kit Enterprise',
    price: 'from $3,000 / mo',
    bundle: [
      'Multi-instance · unlimited viewers per instance',
      'Named support — live production changes: nventr runs Dev Console on your instance while users work; reviewed diffs, UAT, production on your gate; queue and job recovery in the envelope',
      'License phone-home · air-gap option',
      'Platform seats stay a second line unless bundled',
    ],
    who: 'Sales floor on Kit.',
  },
];

export const platformFree = PLATFORM_TIERS.find((t) => t.id === 'free');
export const platformStart = PLATFORM_TIERS.find((t) => t.id === 'start');
export const platformTeam = PLATFORM_TIERS.find((t) => t.id === 'team');
export const platformBusiness = PLATFORM_TIERS.find((t) => t.id === 'business');
export const kitBuild = KIT_TIERS.find((t) => t.id === 'build');
export const appKit = KIT_TIERS.find((t) => t.id === 'kit');
export const kitBusiness = KIT_TIERS.find((t) => t.id === 'kit-business');

// "$15 / mo" → "$15" for prose; "$15/mo" for compact cards. Pages use these
// instead of retyping the number (audit P1-7).
export const dollars = (t) => String(t?.price || '').replace(/\s*\/\s*mo$/, '');
export const perMonth = (t) => `${dollars(t)}/mo`;

// The written bundle (decision two-products-r1): Kit + Platform Team,
// 15% off the sum. Derived so the figure cannot drift from the tiers.
const num = (t) => Number(dollars(t).replace(/[^0-9.]/g, '')) || 0;
export const TEAM_ATTACH = (() => {
  const sum = num(appKit) + num(platformTeam);
  return { price: `$${Math.round(sum * 0.85)} / mo`, note: `15% off $${sum}`, sum };
})();

// Metered prices — the per-unit figures beside the plan fee. List prices
// live here only; /pricing renders these rows.
export const PLATFORM_METERS = [
  ['Seats', 'Plan fee', 'Builders and operators. Viewers free.'],
  ['Runtime Credits', '$0.015–$0.010 / credit', 'Workflow run 2 · conversation 10 · gated commit 2 · HITL 1'],
  ['nQube copilot turns', 'Optional / hosted tokens', 'Build wallet. BYO key = $0.'],
  ['Hosted run tokens', '$0.02 / 1K list', 'Only if nventr hosts the model.'],
  ['Hosted model credits', 'Included allowance, then prepaid', 'Chat, embeddings, image and video at catalog price. Every plan includes a monthly allowance; past it, a prepaid balance you top up. BYO key = $0.'],
];
// One Dev Console run = one agent session, including the worker, judge and
// routine sessions a graph or routine opens. Verifier passes ride along.
// The per-run figure lives here once; module pages compute from it.
export const DEV_RUN_USD = 1.5;
export const KIT_METERS = [
  ['Instance rent', 'Plan fee', 'License for the foundation. Always, including when you host.'],
  ['Dev Console run', `$${DEV_RUN_USD.toFixed(2)} / run`, 'Model-agnostic. One run per agent session, including the workers, judges and routine sessions a graph or routine opens; verifier passes ride along. Overage past the included runs.'],
  ['Instance Runtime Credits', 'If pipelines run on the instance', 'Same recipe as Platform, billed on that box.'],
  ['Compute / storage / egress / GPU', '$0.08 / $0.20 / $0.12 / $2.50', 'Included allowance on a managed host. $0 when you host.'],
  ['Hosted model credits', 'Included allowance, then prepaid', 'The instance calls the same gateway: chat, embeddings, image, video. Kit plans add their own monthly allowance; shared prepaid balance with Platform. BYO key = $0.'],
];

// Kit checkout cards inside an org. Same dollars and bullets as /pricing.
export const APP_PLANS = [
  {
    key: 'starter',
    name: 'Kit',
    price: perMonth(appKit),
    blurb: 'License for the instance. The public site is the front door. nQube on this instance only.',
    bullets: appKit.bundle,
  },
  {
    key: 'growth',
    name: 'Kit Business',
    price: perMonth(kitBusiness),
    blurb: 'Production instance. Org-wide pipelines are still Platform.',
    bullets: KIT_TIERS.find((t) => t.id === 'kit-business').bundle,
  },
];

export function intentCopy(plan) {
  if (plan === 'start') {
    return {
      title: `${platformStart.product} · ${platformStart.price}`,
      body: `${platformStart.helper}. Shared nQube. No dedicated URL, database, or repo. Open an organization, then subscribe on Billing.`,
      next: 'billing',
    };
  }
  if (plan === 'team') {
    return {
      title: `${platformTeam.product} · ${platformTeam.price}`,
      body: `${platformTeam.helper}. Shared nQube. No dedicated URL, database, or repo. Open an organization, then subscribe on Billing.`,
      next: 'billing',
    };
  }
  if (plan === 'build') {
    return {
      title: `${kitBuild.product} · ${kitBuild.price}`,
      body: `${kitBuild.helper}. This is the shared site, not a dedicated instance. The URL, database, and Dev Console are Kit.`,
      next: 'billing',
    };
  }
  if (plan === 'kit') {
    return {
      title: `${appKit.product} · ${appKit.price}`,
      body: `${appKit.helper}. The rent unlocks Dev Console on this instance. Open an organization, then check out under Apps. Org-wide pipelines are still Platform.`,
      next: 'apps',
    };
  }
  return null;
}
